import { Component, OnInit, inject, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { OnboardingStateService } from 'src/app/services/onboarding-state.service';
import { OnboardingService } from 'src/app/services/onboarding.service';
import { ProgressBarComponent } from 'src/app/shared/components/progress-bar/progress-bar.component';

import { EventType, Coverage, TeamSize } from 'src/app/models/onboarding.model';
import type { Positioning } from 'src/app/models/cluster.model';

import {
  EVENT_TYPE_LABELS,
  COVERAGE_OPTIONS,
  TEAM_SIZE_LABELS,
} from 'src/app/models/onboarding.labels';
import { PositioningSelectorComponent } from 'src/app/shared/components/positioning-selector/positioning-selector.component';
import { LogService } from '../../../services/log.service';

interface ContextItinerantData {
  eventTypes: EventType[];
  coverage: Coverage;
  teamSize: TeamSize;
}

@Component({
  selector: 'app-step-context-itinerant',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ProgressBarComponent,
    PositioningSelectorComponent,
  ],
  templateUrl: './step-context-itinerant.component.html',
})
export class StepContextItinerantComponent implements OnInit {
  private log = inject(LogService);
  selectedPositioning: Positioning = 'standard';
  currentStep = 4;
  totalSteps = 6;
  form!: FormGroup;

  readonly DEFAULT_CONTEXT: ContextItinerantData = {
    eventTypes: [EventType.MARKET],
    coverage: Coverage.UNCOVERED,
    teamSize: TeamSize.SOLO,
  };

  teamSizeOptions = Object.entries(TEAM_SIZE_LABELS).map(([id, data]) => ({
    id: id as TeamSize,
    label: data.label,
    description: data.description,
  }));

  eventTypeOptions = Object.entries(EVENT_TYPE_LABELS).map(([id, label]) => ({
    id: id as EventType,
    label,
    description: '', // tu peux compléter si tu veux des descriptions détaillées
  }));

  coverageOptions = COVERAGE_OPTIONS;

  private cdr = inject(ChangeDetectorRef);

  constructor(
    private fb: FormBuilder,
    private onboardingStateService: OnboardingStateService,
    private onboardingService: OnboardingService,
    private router: Router,
  ) {
    this.initForm();
  }

  ngOnInit(): void {
    this.onboardingStateService.setCurrentStep(4);

    // Charger les données précédentes si elles existent
    const data = this.onboardingStateService.getFormData();
    if (!data) return;

    this.form.patchValue({
      eventTypes: data.eventTypes ?? this.DEFAULT_CONTEXT.eventTypes,
      coverage: data.coverage ?? this.DEFAULT_CONTEXT.coverage,
      teamSize: data.teamSize ?? this.DEFAULT_CONTEXT.teamSize,
    });

    // V1 — Restaurer le positionnement
    if (data.positioning) {
      this.selectedPositioning = data.positioning;
    }
  }

  private initForm(): void {
    this.form = this.fb.group({
      eventTypes: [this.DEFAULT_CONTEXT.eventTypes, Validators.required],
      coverage: [this.DEFAULT_CONTEXT.coverage, Validators.required],
      teamSize: [this.DEFAULT_CONTEXT.teamSize, Validators.required],
    });
  }

  toggleEventType(value: EventType): void {
    const current: EventType[] = this.form.value.eventTypes;
    const newEventTypes = current.includes(value)
      ? current.filter((t) => t !== value)
      : [...current, value];
    this.form.patchValue({ eventTypes: newEventTypes });
  }

  isEventTypeSelected(value: EventType): boolean {
    return this.form.value.eventTypes.includes(value);
  }

  selectCoverage(value: Coverage): void {
    this.form.patchValue({ coverage: value });
  }

  selectTeamSize(value: TeamSize): void {
    this.form.patchValue({ teamSize: value });
  }

  isFieldSelected(fieldName: string, value: string): boolean {
    return this.form.get(fieldName)?.value === value;
  }

  continue(): void {
    if (!this.form.valid) {
      this.log.warn('Formulaire invalide');
      return;
    }

    const formData: ContextItinerantData = this.form.value;

    // Sauvegarder dans le state service (sessionStorage)
    this.onboardingStateService.updateFormData({
      eventTypes: formData.eventTypes,
      coverage: formData.coverage,
      teamSize: formData.teamSize,
      positioning: this.selectedPositioning,
    });

    // Sauvegarder en DB via l'API
    this.onboardingService
      .updateOnboardingStep(4, {
        eventTypes: formData.eventTypes,
        coverage: formData.coverage,
        teamSize: formData.teamSize,
        positioning: this.selectedPositioning,
      })
      .subscribe({
        next: () => {
          this.log.debug('✅ Contexte itinérant sauvegardé en DB');
          // Naviguer vers l'étape 5 (produits)
          this.router.navigate(['/onboarding/step/5']);
        },
        error: (err) => {
          this.log.error('❌ Erreur sauvegarde contexte itinérant:', err);
          // Continuer quand même (les données sont en sessionStorage)
          this.router.navigate(['/onboarding/step/5']);
        },
      });
  }

  goBack(): void {
    this.router.navigate(['/onboarding/step/3']);
  }

  get validationMessage(): string {
    const values = this.form.value;
    const isDefault =
      values.eventTypes.length === this.DEFAULT_CONTEXT.eventTypes.length &&
      values.eventTypes.every(
        (v: EventType, i: number) => v === this.DEFAULT_CONTEXT.eventTypes[i],
      ) &&
      values.coverage === this.DEFAULT_CONTEXT.coverage &&
      values.teamSize === this.DEFAULT_CONTEXT.teamSize;

    return isDefault
      ? 'Les valeurs par défaut ont été pré-remplies. Vérifiez si elles vous conviennent ou ajustez-les avant de continuer.'
      : 'Vous avez personnalisé vos choix. Vous pouvez continuer.';
  }
}
